/**
 * Field problems on the unauthenticated forms, named rather than spelled.
 *
 * `lib/validate.ts` stays the authority on the *rules* — the address pattern, the eight
 * characters, the letters-and-numbers requirement. What it cannot own is the *sentence*: it has
 * no translator and no React context, so it names the problem instead of spelling it.
 *
 * These functions are the translation of that name into a catalogue key. The screen resolves it
 * with its own `t()`, so the message a person reads is in their language and the rule behind it
 * is still written down exactly once. Note that nothing here re-tests the value: the reason comes
 * back from the validator, so the two can never drift into disagreeing about which rule failed.
 *
 *     const problem = emailProblem(email)
 *     setError(problem ? t(problem) : undefined)
 */

import type { SimpleTKey } from '../../lib/i18n'
import { validateEmail, validateNewPassword, validatePassword } from '../../lib/validate'
import type { EmailProblem, PasswordProblem } from '../../lib/validate'

const EMAIL_KEY: Readonly<Record<EmailProblem, SimpleTKey>> = {
  required: 'auth.validation.emailRequired',
  malformed: 'auth.validation.emailInvalid',
}

const PASSWORD_KEY: Readonly<Record<PasswordProblem, SimpleTKey>> = {
  required: 'auth.validation.passwordRequired',
  tooShort: 'auth.validation.passwordShort',
  tooLong: 'auth.validation.passwordLong',
  needsMix: 'auth.validation.passwordMix',
}

/** `null` when the address is acceptable. */
export const emailProblem = (value: string): SimpleTKey | null => {
  const problem = validateEmail(value)
  return problem === undefined ? null : EMAIL_KEY[problem]
}

/** Typing an existing password: present, and within the length the server accepts. */
export const passwordProblem = (value: string): SimpleTKey | null => {
  const problem = validatePassword(value)
  return problem === undefined ? null : PASSWORD_KEY[problem]
}

/** Choosing a new one — the same rules plus letters-and-numbers. */
export const newPasswordProblem = (value: string): SimpleTKey | null => {
  const problem = validateNewPassword(value)
  return problem === undefined ? null : PASSWORD_KEY[problem]
}

/** The 0–3 read from `passwordStrength`, as the word shown under the bar. */
export const STRENGTH_KEY: Record<0 | 1 | 2 | 3, SimpleTKey> = {
  0: 'auth.password.strength0',
  1: 'auth.password.strength1',
  2: 'auth.password.strength2',
  3: 'auth.password.strength3',
}
